import React, { useCallback, useContext, useEffect } from "react";
import { connect } from "react-redux";

import { Formik, FormikValues } from "formik";
import { Dispatch, bindActionCreators } from "redux";

import { Overlay, Form, OkButton, CancelLink, TextInput, InputSize, ValidationMessage, Dialog as DialogComponent } from "@plex/react-components";

import { validationMessages } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { Banner } from "ui/app";
import { DialogContainer, DialogNavigationContext } from "ui/app/navigation";
import { useLocalizedValues, useLocalizer } from "ui/hooks";

import { releaseMessages } from "../release.localization";

import { createReleaseActions } from "./CreateRelease.actions";
import styles from "./CreateRelease.module.scss";
import { IReleaseState } from "./CreateRelease.state";

interface ICreateReleaseProps {
  shouldHide: boolean;
  releaseName: string;
  isLoading: boolean;
  notifications: INotification[];
  textValidationError: string;
  createRelease: () => void;
  changeNameFieldValue: (text: string) => void;
  clearState: () => void;
  onReleaseAdded?: () => void;
}

const CreateRelease: React.FC<ICreateReleaseProps> = ({ shouldHide, releaseName, isLoading, notifications, createRelease, changeNameFieldValue, clearState, onReleaseAdded }) => {
  const localize = useLocalizer();
  const dialogNavigationContext = useContext(DialogNavigationContext);

  const localizedValues = useLocalizedValues(() => {
    return {
      name: releaseMessages.property("releaseName"),
      header: releaseMessages.title("createRelease")
    };
  });

  useEffect(() => {
    if (shouldHide) {
      dialogNavigationContext.hideDialog(undefined, () => {
        clearState();
        onReleaseAdded && onReleaseAdded();
      });
    }
  }, [clearState, dialogNavigationContext, onReleaseAdded, shouldHide]);

  const onSubmitClick = useCallback((handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void) => {
    handleSubmit();
  }, []);

  const onChangeNameFieldValue = useCallback(
    (value: string | null) => {
      changeNameFieldValue(value || "");
    },
    [changeNameFieldValue]
  );

  return (
    <DialogContainer
      onHidden={clearState}
      render={dialogProps => {
        return (
          <Formik
            initialValues={{
              releaseName: releaseName || ""
            }}
            validate={() => {
              const errors: any = {};
              if (!releaseName || releaseName.trim() === "") {
                errors.releaseName = localize(validationMessages.error("propertyIsRequired"), {
                  property: localizedValues.name
                });
              }

              return errors;
            }}
            onSubmit={(_: FormikValues) => {
              createRelease();
            }}
          >
            {({ errors, touched, handleSubmit, isSubmitting }) => (
              <DialogComponent {...dialogProps}>
                <DialogComponent.Body>
                  <Overlay show={isLoading} showSpinner />
                  <Banner messages={notifications} />
                  <Form onSubmit={handleSubmit}>
                    <Form.Row>
                      <Form.Body>
                        <Form.FieldPair required labelText={localizedValues.name} className={styles.fieldPairDialog}>
                          <TextInput id="releaseName" name="releaseName" maxLength={150} onValueChange={onChangeNameFieldValue} value={releaseName} size={InputSize.medium} />
                          <ValidationMessage message={isSubmitting || touched.releaseName ? errors.releaseName || "" : ""} />
                        </Form.FieldPair>
                      </Form.Body>
                    </Form.Row>
                  </Form>
                </DialogComponent.Body>
                <DialogComponent.Footer>
                  <CancelLink onClick={dialogProps.onHide} />
                  <OkButton onClick={() => onSubmitClick(handleSubmit)} id="OK" />
                </DialogComponent.Footer>
              </DialogComponent>
            )}
          </Formik>
        );
      }}
    />
  );
};

export default connect(
  (state: { createRelease: IReleaseState }) => {
    return {
      shouldHide: state.createRelease.shouldHide,
      releaseName: state.createRelease.releaseName,
      isLoading: state.createRelease.isLoading,
      notifications: state.createRelease.notifications,
      textValidationError: state.createRelease.textValidationError
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        createRelease: createReleaseActions.addRelease,
        changeNameFieldValue: createReleaseActions.changeNameFieldValue,
        clearState: createReleaseActions.clearState
      },
      dispatch
    );
  }
)(CreateRelease);
